import type { PointerEvent as ReactPointerEvent } from "react";
import { GripVertical, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { BlockInsertPosition } from "./documentEditor";
import { dragHandleRect, type OverlayRect } from "./overlayGeometry";

/**
 * How far the strip runs past the block, top and bottom, so its "+" buttons
 * straddle the block's edges the way the insertion point they open does.
 */
const STRIP_OVERHANG_PX = 10;

/** Height below which the three controls would collide, in px. */
const STRIP_MIN_HEIGHT_PX = 76;

/** Diameter of an add button, in px (`size-5`). */
const ADD_BUTTON_PX = 20;

interface BlockHoverControlsProps {
  /** Container-relative geometry of the hovered block. */
  blockRect: OverlayRect;
  /** True while this block is being drag-reordered. */
  isDragging: boolean;
  /** Starts the drag-reorder session for the hovered block. */
  onDragStart: (event: ReactPointerEvent<HTMLElement>) => void;
  /** Opens the block palette to insert on the given side of the block. */
  onAdd: (position: BlockInsertPosition, anchorRect: OverlayRect) => void;
}

/**
 * The hover controls for a block: a "+" straddling each of its edges that opens
 * the palette to insert above or below it, and between them the grab handle -
 * the ONLY place a reorder drag can start from. They share one strip pinned to
 * the gutter left of the block, spanning its full height, because that strip is
 * what keeps them reachable: it carries the `data-fp-editor-chrome` marker, so
 * the pointer travelling from the block onto any control stays on chrome the
 * whole way and never drops the hover that mounted them. The marker also keeps
 * the chrome itself out of block hit-testing. The strip stays mounted
 * (invisible) during the drag so the pointer capture the grip took on
 * pointerdown survives to the drop.
 * @param blockRect - Container-relative geometry of the hovered block
 * @param isDragging - True while this block is being drag-reordered
 * @param onDragStart - Starts the drag-reorder session
 * @param onAdd - Opens the block palette for the given side
 * @returns The positioned hover controls
 */
export function BlockHoverControls({
  blockRect,
  isDragging,
  onDragStart,
  onAdd,
}: BlockHoverControlsProps) {
  const rect = dragHandleRect(blockRect);
  const top = rect.top - STRIP_OVERHANG_PX;
  const height = Math.max(
    rect.height + STRIP_OVERHANG_PX * 2,
    STRIP_MIN_HEIGHT_PX,
  );
  const addRect = (position: BlockInsertPosition): OverlayRect => ({
    top: position === "before" ? top : top + height - ADD_BUTTON_PX,
    left: rect.left,
    width: rect.width,
    height: ADD_BUTTON_PX,
  });
  return (
    <div
      data-fp-editor-chrome=""
      className={cn(
        "absolute z-40 flex flex-col items-center justify-between",
        isDragging && "opacity-0",
      )}
      style={{ top, left: rect.left, width: rect.width, height }}
    >
      <AddButton
        label="Add content above"
        onClick={() => onAdd("before", addRect("before"))}
      />
      <button
        type="button"
        aria-label="Drag to reorder block"
        title="Drag to reorder"
        className="flex h-6 w-5 shrink-0 cursor-grab items-center justify-center rounded border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground active:cursor-grabbing"
        onPointerDown={onDragStart}
        onClick={(event) => event.stopPropagation()}
      >
        <GripVertical className="size-3.5" />
      </button>
      <AddButton
        label="Add content below"
        onClick={() => onAdd("after", addRect("after"))}
      />
    </div>
  );
}

interface AddButtonProps {
  /** Accessible label and tooltip naming the side content lands on. */
  label: string;
  /** Opens the block palette for that side. */
  onClick: () => void;
}

/**
 * One "+" control in the hover strip.
 * @param label - Accessible label and tooltip
 * @param onClick - Opens the block palette for that side
 * @returns The add button
 */
function AddButton({ label, onClick }: AddButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="flex size-5 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground"
      onClick={onClick}
    >
      <Plus className="size-3.5" />
    </button>
  );
}
